/* The Profil tab's own boxes — the avatar, the photograph's picker, the address line and the
 * password disclosure. Everything a second tab also draws (the groups, the rows, the three button
 * paints) is `AccountScreen.module.css`'s and is read from there, so no declaration lives in both.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The photograph's row body: the disc and the sentence beside it. Only the arrangement is this
 * file's; the body's own ink and size are `.rowBody`'s. */
.photoBody {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-account-row-avatar);
  height: var(--sz-account-row-avatar);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
}

/* The disc while the photograph is being prepared and sent. `BrandLoader` sizes itself from `--m`,
 * so the mark is resized through that hook rather than by restating the component's own `width` —
 * and the selector is 0-2-0 so it outranks the loader's own rule by SPECIFICITY rather than by
 * chunk order. */
.photoBody .avatarBusy {
  --m: var(--sz-account-row-avatar);
}

/* The photograph's GEOMETRY; `photoBox` supplies the fit and the neutral ground, which is also the
 * error state — the box exists before the fetch does, so nothing moves when the file lands. */
.photo {
  display: block;
  flex: none;
  width: var(--sz-account-row-avatar);
  height: var(--sz-account-row-avatar);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
}

/* „Zmień…" — a label wearing the secondary pill, with the file input inside it. The ring goes on
 * the LABEL, because the input is the focused element and is not drawn. */
.pick {
  position: relative;
}

.pick:has(:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.pick:has([aria-disabled='true']) {
  color: var(--text-3);
  cursor: not-allowed;
}

/* The file input itself. Not `display: none`, which takes a control out of the tab order and out of
 * the accessibility tree — the label is the visible affordance and this is the real control, so it
 * stays focusable and is simply given no area of its own to paint. */
.fileInput {
  position: absolute;
  width: var(--border-hairline);
  height: var(--border-hairline);
  overflow: hidden;
  opacity: 0;
}

/* The sign-in address, in body ink, with the lock that says it does not change here. */
.email {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-button-sm);
  color: var(--text);
}

.lock {
  display: block;
  flex: none;
  width: var(--sz-account-glyph);
  height: var(--sz-account-glyph);
  color: var(--text-2);
}

.note {
  display: block;
  margin-top: var(--space-1px);
  font-size: var(--fs-micro);
}

.mask {
  font-size: var(--fs-button-sm);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text);
}

/* The password disclosure: the two fields, the four rules and the two answers, inside the group. */
.passwordPanel {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding-block: var(--space-2);
}

.pair {
  display: grid;
  gap: var(--space-14px);
  grid-template-columns: 1fr 1fr;
}

.passwordFoot {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

/* The four rules, two across.
 *
 * They are NOT `features/auth-by-email`'s `PasswordStrength`, and that is a deliberate departure:
 * that component draws a four-segment meter and a tier word above the list, and this canon draws
 * neither. What IS shared is everything that could drift: the rule itself is `assessPassword`, and
 * the four words are upstream's own `auth.pw.*`. */
.rules {
  display: grid;
  flex: 1;
  gap: var(--space-1-5px) var(--space-4);
  grid-template-columns: 1fr 1fr;
  margin: var(--space-0);
  padding: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.rule {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
  list-style: none;
}

/* The mark is a 7px ring that fills when the rule is met. State is carried by the ROLE as well, so
 * this is never meaning in colour alone. */
.ruleMark {
  flex: none;
  width: var(--space-7px);
  height: var(--space-7px);
  border: var(--border-thick) solid var(--text-3);
  border-radius: var(--r-circle);
}

.ruleMet {
  color: var(--good-ink);
}

.ruleMet .ruleMark {
  background: var(--good);
  border-color: var(--good);
}
